import React, { useState, useEffect, useRef } from 'react';
import CreditsBadge from '../components/CreditsBadge';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import UserMenu from '../components/UserMenu';
import NavBrand from '../components/NavBrand';
import HeaderSearch from '../components/HeaderSearch';
import NotificationBell from '../components/NotificationBell';
import MessagesButton from '../components/MessagesButton';
import DesignerSettingsCard from '../components/DesignerSettingsCard';
import ShopSettingsCard from '../components/ShopSettingsCard';
import AvatarPicker from '../components/AvatarPicker';
import LocationPicker from '../components/LocationPicker';
import { updateUserAvatar, updateUserLocation } from '../firebase/userService';
import { USER_TYPES } from '../constants/designer';
import './Dashboard.css';
import './auth.css';
import './Settings.css';
import BackBar from '../components/BackBar';

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').slice(0, 2).map(w => w[0].toUpperCase()).join('');
}

// Returns { level 0-5, color }. Label looked up via t() in render. Mirrors
// the same small helper duplicated across RegisterForm/ForgotPassword — this
// codebase keeps these per-page rather than sharing a utils module.
function getPasswordStrength(pw) {
  if (!pw)          return { level: 0, color: '#e5e7eb' };
  if (pw.length < 6) return { level: 1, color: '#ef4444' };
  let score = 1;
  if (pw.length >= 8)           score++;
  if (/[A-Z]/.test(pw))        score++;
  if (/[0-9]/.test(pw))        score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 2) return { level: 2, color: '#f97316' };
  if (score === 3) return { level: 3, color: '#eab308' };
  if (score === 4) return { level: 4, color: '#22c55e' };
  return              { level: 5, color: '#10b981' };
}

const STRENGTH_KEYS = ['', 'auth_pw_tooshort', 'auth_pw_weak', 'auth_pw_fair', 'auth_pw_good', 'auth_pw_strong'];

function EyeOpen() {
  return (
    <svg viewBox="0 0 24 24" fill="none" width="18" height="18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  );
}

function EyeClosed() {
  return (
    <svg viewBox="0 0 24 24" fill="none" width="18" height="18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

export default function Settings() {
  const { user, profile, refreshProfile, updateFullName, changePassword } = useAuth();
  // Designers and shops manage their picture inside their own profile block
  // below, so this standalone picker is for customers only — two savers for one
  // field would be a race waiting to happen.
  const isDesigner = profile?.userType === USER_TYPES.DESIGNER;
  const isShop     = profile?.userType === USER_TYPES.SHOP;
  const isCustomer = !isDesigner && !isShop;
  const [avatarSaving, setAvatarSaving] = useState(false);
  async function handleAvatarChange(url) {
    setAvatarSaving(true);
    try { await updateUserAvatar(user.uid, url); await refreshProfile(); }
    finally { setAvatarSaving(false); }
  }
  const navigate = useNavigate();
  // Named to stay out of the way of `location`, which on this page is the
  // user's own town — the field being edited, not the router's.
  const routerLocation = useLocation();
  const { t } = useT();

  const currentName = user?.displayName || user?.email?.split('@')[0] || '';
  const [name, setName]       = useState(currentName);
  const [saving, setSaving]   = useState(false);
  const [toast, setToast]     = useState(null);
  const [nameError, setNameError] = useState('');

  // A customer's location. Seeded from the profile once it has loaded — the
  // first render happens before the document arrives, so a plain initialiser
  // would latch an empty string forever.
  const [location, setLocation] = useState(profile?.location || '');
  // The full picked line. `location` stays the coarse search key — see
  // LocationPicker — so both are held and both are saved.
  const [locationAddress, setLocationAddress] = useState(profile?.locationAddress || '');
  const [locationCoords, setLocationCoords] = useState(profile?.locationCoords || null);
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || !profile) return;
    seededRef.current = true;
    setLocation(profile.location || '');
    setLocationAddress(profile.locationAddress || '');
    setLocationCoords(profile.locationCoords || null);
  }, [profile]);

  const initials = getInitials(currentName || 'Designer');
  const email     = user?.email || '';
  // Google-only accounts have no password on file — nothing to change here.
  const hasPasswordProvider = user?.providerData?.some(p => p.providerId === 'password');

  // ── Change Password (separate card, separate state/toast) ───────────────
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword,     setNewPassword]     = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent,     setShowCurrent]     = useState(false);
  const [showNew,         setShowNew]         = useState(false);
  const [showConfirm,     setShowConfirm]     = useState(false);
  const [pwSaving,        setPwSaving]        = useState(false);
  const [pwToast,         setPwToast]         = useState(null);
  const [pwFieldErrors,   setPwFieldErrors]   = useState({});
  const strength = getPasswordStrength(newPassword);

  function showToast(msg, type = 'error') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  }

  function showPwToast(msg, type = 'error') {
    setPwToast({ msg, type });
    setTimeout(() => setPwToast(null), 4000);
  }

  async function handleSave(e) {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length < 2) { setNameError(t('auth_err_name')); return; }
    setNameError('');
    setSaving(true);
    try {
      // Name and location are one save, because they are one form. The name
      // goes first: it writes the Auth profile too, and if that fails there is
      // nothing worth persisting about the location either.
      await updateFullName(trimmed);
      await updateUserLocation(user.uid, location, locationCoords, locationAddress);
      await refreshProfile();

      // ── ARRIVED HERE AS A SIGN-UP STEP ────────────────────────────────────
      // /account-type sends a new CUSTOMER here to add the picture, name and
      // location that the one-step sign-ins never asked for, and passes the
      // page they were originally trying to reach. Saving finishes the sign-up,
      // so it resumes that journey rather than stranding them on Settings.
      //
      // Only when a destination was actually handed over: an ordinary visit to
      // Settings has no `from` and must stay exactly where it is.
      const resume = routerLocation.state?.from?.pathname;
      if (resume) { navigate(resume, { replace: true }); return; }

      showToast(t('set_saved'), 'success');
    } catch (err) {
      // Surface the real Firebase error text (e.g. "Missing or insufficient
      // permissions.") instead of a generic message — updateFullName writes
      // to two places (Auth profile + Firestore), and knowing WHICH one
      // failed, and why, is the difference between guessing and fixing it.
      console.error('Failed to update full name:', err);
      showToast(err?.message || t('set_save_failed'));
    } finally {
      setSaving(false);
    }
  }

  function validatePasswordFields() {
    const errors = {};
    if (!currentPassword)                     errors.current = t('auth_err_pass_req');
    if (!newPassword)                         errors.next    = t('auth_err_pass_req');
    else if (newPassword.length < 6)         errors.next    = t('auth_err_pass_min');
    if (!confirmPassword)                     errors.confirm = t('auth_err_conf_req');
    else if (confirmPassword !== newPassword) errors.confirm = t('auth_err_conf_match');
    setPwFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleChangePassword(e) {
    e.preventDefault();
    if (!validatePasswordFields()) return;
    setPwSaving(true);
    try {
      await changePassword(currentPassword, newPassword);
      showPwToast(t('set_password_changed'), 'success');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      const code = err.code;
      const msg = (code === 'auth/wrong-password' || code === 'auth/invalid-credential')
        ? t('set_wrong_current_password')
        : code === 'auth/weak-password'
          ? t('auth_err_pass_min')
          : t('set_save_failed');
      showPwToast(msg);
    } finally {
      setPwSaving(false);
    }
  }

  // Save enables on EITHER field changing — gating it on the name alone left
  // the button dead after editing only the location.
  const dirty = name.trim().length > 0
    && (name.trim() !== currentName || location !== (profile?.location || ''));
  const pwDirty = currentPassword.length > 0 && newPassword.length > 0 && confirmPassword.length > 0;

  return (
    <div className="dash-root">
      {/* ── TOP NAV (same shell as Dashboard, so Settings reads as part of
          the same authenticated app rather than a separate mini-site) ── */}
      <header className="dash-nav">
        <NavBrand />

        <div className="dash-nav-right">

          <HeaderSearch />
          <NotificationBell />
          <CreditsBadge />
          <MessagesButton />
          <UserMenu />
        </div>
      </header>

      {/* ── MAIN ── */}
      {/* Designers have far more to edit (profile + two galleries), so the
          560px column that suits a name-and-password page makes theirs
          enormously tall. Wider lets the fields sit side by side instead. */}
      <BackBar width={isCustomer ? 860 : 1120} />

      <main className={`dash-main set-main${isCustomer ? '' : ' set-main--wide'}`}>
        <h2 className="dash-section-title">{t('set_title')}</h2>

        {/* Account card — CUSTOMERS ONLY.
            A designer edits the same fullName inside the Designer profile card
            below, and both write users/{uid}.fullName. Two inputs bound to one
            field is not just clutter: whichever form saved last would silently
            win, so a name typed here could be overwritten by a stale value
            there. One editor per field removes the race entirely. */}
        {isCustomer && (
        <div className="set-card">
            <div className="set-card-identity">
              <div className="dash-avatar dash-avatar--lg">
                {profile?.avatarUrl
                  ? <img src={profile.avatarUrl} alt=""
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%', display: 'block' }} />
                  : initials}
              </div>
              <div>
                <div className="set-identity-name">{currentName || t('set_no_name')}</div>
                <div className="dash-user-email">{email}</div>
              </div>
            </div>
  
            {toast && (
              <div className={`af-toast af-toast--${toast.type}`} role="alert">
                <span className="af-toast__icon">{toast.type === 'error' ? '⚠' : '✓'}</span>
                {toast.msg}
              </div>
            )}
  
            {isCustomer && (
              <div className="af-field">
                <AvatarPicker
                  value={profile?.avatarUrl || ''}
                  onChange={handleAvatarChange}
                  disabled={avatarSaving}
                />
              </div>
            )}
  
            <form onSubmit={handleSave} noValidate>
              <div className="af-field">
                <label className="af-label" htmlFor="set-name">{t('set_fullname_label')}</label>
                <div className="af-input-wrap">
                  <span className="af-input-icon">
                    <svg viewBox="0 0 24 24" fill="none" width="16" height="16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
                    </svg>
                  </span>
                  <input
                    id="set-name"
                    className={`af-input${nameError ? ' af-input--error' : ''}`}
                    type="text"
                    placeholder={t('auth_name_ph')}
                    value={name}
                    onChange={e => { setName(e.target.value); setNameError(''); }}
                    autoComplete="name"
                    disabled={saving}
                  />
                </div>
                {nameError && <p className="af-field-error">{nameError}</p>}
              </div>

              {/* Same picker the designers use — one map, one geocoder, one set
                  of quirks to maintain. */}
              <div className="af-field">
                <label className="af-label">{t('ds_location')}</label>
                <LocationPicker
                  value={location}
                  address={locationAddress}
                  coords={locationCoords}
                  onChange={({ location: loc, locationAddress: addr, locationCoords: c }) => {
                    setLocation(loc || '');
                    setLocationAddress(addr || '');
                    setLocationCoords(c || null);
                  }}
                  disabled={saving}
                />
              </div>

              <button className="af-btn set-save-btn" type="submit" disabled={saving || !dirty}>
                {saving && <span className="af-spinner" />}
                {saving ? t('set_saving') : t('set_save')}
              </button>
            </form>
          </div>
        )}

        {/* ── Published profile ── one card per publishing role.
            A customer's settings are their own three fields and nothing else.
            Which card appears is decided by `userType` alone, so the two can
            never both render and fight over `fullName`. */}
        {isDesigner && <DesignerSettingsCard />}
        {isShop && <ShopSettingsCard />}

        {/* Notifications used to have a card here. The switch now lives in the
            account dropdown (see NotificationMenuItem), which is reachable from
            every page rather than only this one. */}

        {/* ── Change Password ── */}
        <div className="set-card" style={{ marginTop: 20 }}>
          <div className="set-identity-name" style={{ marginBottom: hasPasswordProvider ? 20 : 8 }}>
            {t('set_change_password_title')}
          </div>

          {!hasPasswordProvider ? (
            <p className="af-subtitle" style={{ margin: 0 }}>{t('set_google_only_password')}</p>
          ) : (
            <>
              <form onSubmit={handleChangePassword} noValidate>
                {/* Current Password */}
                <div className="af-field">
                  <label className="af-label" htmlFor="set-current-pw">{t('set_current_password_label')}</label>
                  <div className="af-input-wrap">
                    <span className="af-input-icon">
                      <svg viewBox="0 0 24 24" fill="none" width="16" height="16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0110 0v4"/>
                      </svg>
                    </span>
                    <input
                      id="set-current-pw"
                      className={`af-input af-input--has-action${pwFieldErrors.current ? ' af-input--error' : ''}`}
                      type={showCurrent ? 'text' : 'password'}
                      placeholder={t('set_current_password_ph')}
                      value={currentPassword}
                      onChange={e => { setCurrentPassword(e.target.value); setPwFieldErrors(p => ({ ...p, current: '' })); }}
                      autoComplete="current-password"
                      disabled={pwSaving}
                    />
                    <button type="button" className="af-input-action" onClick={() => setShowCurrent(s => !s)} tabIndex={-1}>
                      {showCurrent ? <EyeClosed /> : <EyeOpen />}
                    </button>
                  </div>
                  {pwFieldErrors.current && <p className="af-field-error">{pwFieldErrors.current}</p>}
                </div>

                {/* New Password */}
                <div className="af-field">
                  <label className="af-label" htmlFor="set-new-pw">{t('set_new_password_label')}</label>
                  <div className="af-input-wrap">
                    <span className="af-input-icon">
                      <svg viewBox="0 0 24 24" fill="none" width="16" height="16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0110 0v4"/>
                      </svg>
                    </span>
                    <input
                      id="set-new-pw"
                      className={`af-input af-input--has-action${pwFieldErrors.next ? ' af-input--error' : ''}`}
                      type={showNew ? 'text' : 'password'}
                      placeholder={t('auth_password_min_ph')}
                      value={newPassword}
                      onChange={e => { setNewPassword(e.target.value); setPwFieldErrors(p => ({ ...p, next: '' })); }}
                      autoComplete="new-password"
                      disabled={pwSaving}
                    />
                    <button type="button" className="af-input-action" onClick={() => setShowNew(s => !s)} tabIndex={-1}>
                      {showNew ? <EyeClosed /> : <EyeOpen />}
                    </button>
                  </div>
                  {pwFieldErrors.next && <p className="af-field-error">{pwFieldErrors.next}</p>}

                  {newPassword.length > 0 && (
                    <div className="af-strength">
                      <div className="af-strength-bars">
                        {[1, 2, 3, 4].map(n => (
                          <div
                            key={n}
                            className="af-strength-bar"
                            style={{ background: strength.level >= n + 1 ? strength.color : 'var(--site-border-2)' }}
                          />
                        ))}
                      </div>
                      <span className="af-strength-label" style={{ color: strength.level > 1 ? strength.color : 'var(--site-text-3)' }}>
                        {t(STRENGTH_KEYS[strength.level])}
                      </span>
                    </div>
                  )}
                </div>

                {/* Confirm New Password */}
                <div className="af-field">
                  <label className="af-label" htmlFor="set-confirm-pw">{t('auth_confirm_label')}</label>
                  <div className="af-input-wrap">
                    <span className="af-input-icon">
                      <svg viewBox="0 0 24 24" fill="none" width="16" height="16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                      </svg>
                    </span>
                    <input
                      id="set-confirm-pw"
                      className={`af-input af-input--has-action${pwFieldErrors.confirm ? ' af-input--error' : ''}`}
                      type={showConfirm ? 'text' : 'password'}
                      placeholder={t('auth_confirm_ph')}
                      value={confirmPassword}
                      onChange={e => { setConfirmPassword(e.target.value); setPwFieldErrors(p => ({ ...p, confirm: '' })); }}
                      autoComplete="new-password"
                      disabled={pwSaving}
                    />
                    <button type="button" className="af-input-action" onClick={() => setShowConfirm(s => !s)} tabIndex={-1}>
                      {showConfirm ? <EyeClosed /> : <EyeOpen />}
                    </button>
                  </div>
                  {pwFieldErrors.confirm && <p className="af-field-error">{pwFieldErrors.confirm}</p>}
                </div>

                {/* Same centred block as the designer card, so both forms
                    confirm in the same place rather than each inventing one. */}
                <div className="ds-actions">
                  <button className="af-btn set-save-btn" type="submit" disabled={pwSaving || !pwDirty}>
                    {pwSaving && <span className="af-spinner" />}
                    {pwSaving ? t('set_changing_password') : t('set_change_password_btn')}
                  </button>

                  {pwToast && (
                    <div className={`af-toast af-toast--${pwToast.type} ds-toast`} role="status" aria-live="polite">
                      <span className="af-toast__icon">{pwToast.type === 'error' ? '⚠' : '✓'}</span>
                      {pwToast.msg}
                    </div>
                  )}
                </div>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
